<template>
  <section class="steps-pay-finish-container">
    <div class="block" v-show="hasErrors" ref="generalErrorBlock">
      <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage" id="error-container"></error-display-component>
    </div>
    <form id="payment-form" data-vv-scope="payment-form">
      <div class="columns is-multiline">
        <div class="column is-6">
          <div v-if="hasGooglePayInitialized" id="google-pay-button" @click="handlePaymentMethodSubmissionGoogle"></div>
        </div>
        <div class="column is-6">
          <div v-if="hasApplePayInitialized" id="apple-pay-button" @click="handlePaymentMethodSubmissionApple"></div>
        </div>
        <div class="column is-6">
          <div class="field">
            <label class="label">First Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
            <div class="control">
              <input name="firstName" type="text" placeholder="e.g John" v-model="printRequest.payerFirstName" v-validate="{'required': true, 'max': 150}" :class="{'input': true, 'is-danger': errors.has('payment-form.' + 'firstName')}" data-vv-as="first name">
            </div>
            <span v-show="errors.has('payment-form.' + 'firstName')" class="help is-danger">
              <i v-show="errors.has('payment-form.' + 'firstName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('payment-form.' + 'firstName') }}
            </span>
          </div>
        </div>
        <div class="column is-6">
          <div class="field">
            <label class="label">Last Name<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
            <div class="control">
              <input name="lastName" type="text" placeholder="e.g Smith" v-model="printRequest.payerLastName" v-validate="{'required': true, 'max': 150}" :class="{'input': true, 'is-danger': errors.has('payment-form.' + 'lastName')}" data-vv-as="last name">
            </div>
            <span v-show="errors.has('payment-form.' + 'lastName')" class="help is-danger">
              <i v-show="errors.has('payment-form.' + 'lastName')" class="fas fa-exclamation-triangle"></i>{{ errors.first('payment-form.' + 'lastName') }}
            </span>
          </div>
        </div>
        <div class="column is-12">
          <div id="card-container"></div>
        </div>
        <div class="column is-12">
          <button id="card-button" type="button" @click="handlePaymentMethodSubmissionCard" class="button is-primary">Pay {{amount | currency}}</button>
        </div>
      </div>
    </form>
    <div id="payment-status-container" class="mt-10"></div>
    <b-loading :is-full-page="false" :active.sync="isLoading" :canCancel="false"></b-loading>

    <b-modal :active.sync="isPaymentInProgressActive" scroll="keep" :has-modal-card="true" v-if="isPaymentInProgressActive" :canCancel="false" style="z-index:20000">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Payment In Progress</p>
        </header>
        <section class="modal-card-body has-background-grey-lighter">
          <div class="is-flex-tablet notification is-primary is-light notification-container" v-if="showPaymentStepWarningMessage">
            <button class="delete" @click="showPaymentStepWarningMessage = false"></button>
            <i class="fas fa-info-circle fa-lg is-hidden-tablet"></i>
            <i class="fas fa-info-circle fa-2x is-hidden-mobile"></i>
            <span class="ml-10 completed-title"> <strong>Do not close your browser.</strong> Once all the payment steps has completed this window will close automatically. This process will take several minutes to complete on slower internet connections.</span>
          </div>
          <div class="tile is-ancestor">
            <div class="tile is-vertical">
              <div class="tile">
                <div class="tile is-parent" :class="verifyInformationContainerClass">
                  <article class="tile is-child notification" :class="verifyInfoClass">
                    <p class="title">1 <i class="fal fa-user-check"></i><i class="fas fa-check-circle is-pulled-right" v-if="!isVerifyInfoStepActive"></i></p>
                    <p class="subtitle">Verifying Information...</p>
                    <div class="content">
                      <progress class="progress is-small is-primary" max="100" v-if="isVerifyInfoStepActive"></progress>
                    </div>
                  </article>
                </div>
                <div class="tile is-parent" :class="processPaymentContainerClass">
                  <article class="tile is-child notification" :class="processPaymentClass">
                    <p class="title">2 <i class="fal fa-sack-dollar"></i><i class="fas fa-check-circle is-pulled-right" v-if="!isProcessPaymentStepActive"></i></p>
                    <p class="subtitle">Processing Payment...</p>
                    <div class="content">
                      <progress class="progress is-small is-primary" max="100" v-if="isProcessPaymentStepActive"></progress>
                    </div>
                  </article>
                </div>
                <div class="tile is-parent" :class="syncTransStepContainerClass" ref="processPaymentContainer">
                  <article class="tile is-child notification" :class="syncTransClass">
                    <p class="title">3 <i class="fal fa-cloud-download"></i><i class="fas fa-check-circle is-pulled-right" v-if="!isSyncTransStepActive"></i></p>
                    <p class="subtitle">Syncing Transaction...</p>
                    <div class="content">
                      <progress class="progress is-small is-primary" max="100" v-if="isSyncTransStepActive"></progress>
                    </div>
                  </article>
                </div>
              </div>
              <div class="tile is-parent">
                <article class="tile is-child" ref="syncTransStepContainer is-flex">
                  <div class="content is-flex">
                    <progress class="progress is-large is-primary" max="100" :value="overallProgressPercentage"></progress>
                    <span class="tag ml-10 is-primary is-light">
                      {{overallProgressPercentage}}%
                    </span>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<script>
import squareService from '@/services/squareService'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'

export default {
  name: 'SquareWebPayControl',
  components: {
    'error-display-component': ErrorDisplayComponent
  },
  props: {
    printRequest: {
      type: Object,
      default: undefined
    },
    dealerId: {
      type: Number,
      default: 1
    },
    paymentGuid: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      card: undefined,
      isLoading: false,
      serverErrors: [],
      serverErrorMessage: '',
      applePay: undefined,
      googlePay: undefined,
      paymentMethod: undefined,
      locations: undefined,
      hasApplePayInitialized: true,
      hasGooglePayInitialized: true,
      skipNameValidation: false,
      localGuid: this.paymentGuid,
      isPaymentInProgressActive: false,
      paymentStep: 1,
      showPaymentStepWarningMessage: true
    }
  },
  computed: {
    amountInCents: function () {
      return this.amount * 100
    },
    locationId: function () {
      const squarelocation = this.locations.find(x => x.name.toLowerCase() === this.sqLocationName.toLowerCase())
      return squarelocation ? squarelocation.id : ''
    },
    amount: function () {
      return this.printRequest && this.printRequest.amount ? this.printRequest.amount : undefined
    },
    sqLocationName: function () {
      return this.printRequest ? this.printRequest.sqLocationNamePrRo : ''
    },
    hasErrors: function () {
      return this.serverErrors.length > 0 || this.serverErrorMessage !== ''
    },
    verifyInfoClass: function () {
      const classToAttach = { }
      classToAttach['is-info'] = false
      const verifyInfoClass = this.isVerifyInfoStepActive ? 'is-info' : 'is-primary'
      classToAttach[verifyInfoClass] = true
      return classToAttach
    },
    isVerifyInfoStepActive: function () {
      return this.paymentStep === 1
    },
    processPaymentClass: function () {
      const classToAttach = { }
      classToAttach['is-light'] = false
      classToAttach['is-info'] = false
      const processPaymentClass = this.isProcessPaymentStepActive ? 'is-info' : this.isVerifyInfoStepActive ? 'has-text-grey-lighter' : 'is-primary'
      classToAttach[processPaymentClass] = true
      return classToAttach
    },
    isProcessPaymentStepActive: function () {
      return this.paymentStep === 2
    },
    syncTransClass: function () {
      const classToAttach = { }
      classToAttach['is-light'] = false
      classToAttach['is-info'] = false
      const syncTransClass = this.isSyncTransStepActive ? 'is-info' : this.isVerifyInfoStepActive || this.isProcessPaymentStepActive ? 'has-text-grey-lighter' : 'is-primary'
      classToAttach[syncTransClass] = true
      return classToAttach
    },
    isSyncTransStepActive: function () {
      return this.paymentStep === 3
    },
    overallProgressPercentage: function () {
      if (this.isVerifyInfoStepActive) return '0'
      if (this.isProcessPaymentStepActive) return '32'
      if (this.isSyncTransStepActive) return '66'
      return '100'
    },
    verifyInformationContainerClass: function () {
      const classToAttach = { }
      const p = 'p30'
      classToAttach[p] = !this.isVerifyInfoStepActive
      return classToAttach
    },
    processPaymentContainerClass: function () {
      const classToAttach = { }
      const p = 'p30'
      classToAttach[p] = !this.isProcessPaymentStepActive
      return classToAttach
    },
    syncTransStepContainerClass: function () {
      const classToAttach = { }
      const p = 'p30'
      classToAttach[p] = !this.isSyncTransStepActive
      return classToAttach
    }
  },
  methods: {
    async initializeCard (payments) {
      const card = await payments.card()
      await card.attach('#card-container')
      return card
    },
    async createPayment (token) {
      this.paymentStep = 1
      this.isPaymentInProgressActive = true
      const payRequest = {
        sqCreatePaymentRequest: {
          sourceId: token,
          locationId: this.locationId,
          amountMoney: {
            amount: this.amountInCents,
            currency: 'USD'
          }
        },
        printRequestId: this.printRequest.id,
        firstName: this.printRequest.payerFirstName,
        lastName: this.printRequest.payerLastName,
        email: this.printRequest.payerEmail,
        roNumber: this.printRequest.roNumber,
        paymentGuid: this.localGuid,
        dealerId: this.dealerId
      }

      let paymentResponse = await squareService.verifyPayer(payRequest)
      if (paymentResponse.errors) {
        this.serverErrors = paymentResponse.errors
        throw new Error()
      } else if (paymentResponse.message) {
        throw new Error(paymentResponse.message)
      } else if (paymentResponse.paymentInProgress !== undefined && paymentResponse.paymentInProgress === true) {
        throw new Error('A payment could already be pending therefore a possible double payment was avoided. Please contact your consultant to resolve this matter.')
      }

      this.paymentStep = 2
      this.scrollToProcessPaymentContainer()
      paymentResponse = await squareService.processPayment(paymentResponse.printRequestPaymentModel)
      if (paymentResponse.errors) {
        this.serverErrors = paymentResponse.errors
        throw new Error()
      } else if (paymentResponse.message) {
        throw new Error(paymentResponse.message)
      } else if (paymentResponse.paymentInProgress !== undefined && paymentResponse.paymentInProgress === true) {
        throw new Error('A payment could already be pending therefore a possible double payment was avoided. Please contact your consultant to resolve this matter.')
      }

      this.paymentStep = 3
      this.scrollToSyncTransStepContainer()
      paymentResponse = await squareService.syncPayment(paymentResponse.printRequestPaymentModel)
      if (paymentResponse.errors) {
        this.serverErrors = paymentResponse.errors
        throw new Error()
      } else if (paymentResponse.message) {
        throw new Error(paymentResponse.message)
      }

      this.paymentStep = 4 // this step doesnt do anything other than set the overallProgressPercentage to 100 for visual aspect
      await this.delay()
      this.paymentStep = 1
      this.isPaymentInProgressActive = false

      return paymentResponse
    },
    async tokenize (paymentMethod) {
      const tokenResult = await paymentMethod.tokenize()
      if (tokenResult.status === 'OK') {
        return tokenResult.token
      } else {
        let errorMessage = 'Tokenization failed with status: ' + tokenResult.status
        if (tokenResult.errors) {
          this.serverErrors = tokenResult.errors
          errorMessage = ''
        }
        throw new Error(errorMessage)
      }
    },
    displayPaymentResults: function (status) {
      const statusContainer = document.getElementById('payment-status-container')
      if (status === 'SUCCESS') {
        statusContainer.classList.remove('is-failure')
        statusContainer.classList.add('is-success')
        const paymentForm = document.getElementById('payment-form')
        paymentForm.style.display = 'none'
      } else {
        statusContainer.classList.remove('is-success')
        statusContainer.classList.add('is-failure')
      }
      statusContainer.style.visibility = 'visible'
    },
    async handlePaymentMethodSubmission (event) {
      if (this.skipNameValidation) {
        await this.paymentMethodSubmission(event)
        this.skipNameValidation = false
      } else {
        this.$validator.validateAll('payment-form').then(async (result) => {
          if (result) {
            await this.paymentMethodSubmission(event)
          }
        })
      }
    },
    async handlePaymentMethodSubmissionCard (event) {
      this.paymentMethod = this.card
      await this.handlePaymentMethodSubmission(event)
    },
    async handlePaymentMethodSubmissionGoogle (event) {
      this.paymentMethod = this.googlePay
      this.skipNameValidation = true
      await this.handlePaymentMethodSubmission(event)
    },
    async handlePaymentMethodSubmissionApple (event) {
      this.paymentMethod = this.applePay
      this.skipNameValidation = true
      await this.handlePaymentMethodSubmission(event)
    },
    async paymentMethodSubmission (event) {
      this.serverErrorMessage = ''
      this.serverErrors = []
      event.preventDefault()
      const cardButton = document.getElementById('card-button')
      try {
        // disable the submit button as we await tokenization and make a payment request.
        cardButton.disabled = true
        this.isLoading = true
        const token = await this.tokenize(this.paymentMethod)
        await this.createPayment(token)
        this.displayPaymentResults('SUCCESS')
        this.$emit('on-success')
        this.isLoading = false
      } catch (error) {
        console.log('error:', error)
        cardButton.disabled = false
        this.isLoading = false
        this.isPaymentInProgressActive = false
        this.serverErrorMessage = error && error.message ? error.message : (error && error.data) ? error.data.exceptionMessage : ''
        this.displayPaymentResults('FAILURE')
        this.scrollToErrorBlock()
        this.localGuid = this.$uuid.v4()
      }
    },
    buildPaymentRequest: function (payments) {
      return payments.paymentRequest({
        countryCode: 'US',
        currencyCode: 'USD',
        total: {
          amount: this.amount.toString(),
          label: 'Total'
        }
      })
    },
    async initializeApplePay (payments) {
      const paymentRequest = this.buildPaymentRequest(payments)
      const applePay = await payments.applePay(paymentRequest)
      // Note: You do not need to `attach` applePay.
      return applePay
    },
    async initializeGooglePay (payments) {
      const paymentRequest = this.buildPaymentRequest(payments)
      const googlePay = await payments.googlePay(paymentRequest)
      await googlePay.attach('#google-pay-button')
      return googlePay
    },
    async getLocations () {
      this.isLoading = true
      this.serverErrorMessage = ''
      this.serverErrors = []

      await squareService.getLocations(this.dealerId).then(response => {
        this.isLoading = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.locations = response.activeLocations
        }
      }).catch((error) => {
        this.isLoading = false
        this.serverErrorMessage = error.message
      })
    },
    scrollToErrorBlock: function () {
      const that = this
      this.isPricingCollapsed = false
      this.$nextTick(() => {
        setTimeout(function () {
          const element = that.$refs.generalErrorBlock
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    },
    async delay () {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(2)
        }, 2500)
      })
    },
    scrollToProcessPaymentContainer: function () {
      const that = this
      this.$nextTick(() => {
        setTimeout(function () {
          const element = that.$refs.processPaymentContainer
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    },
    scrollToSyncTransStepContainer: function () {
      const that = this
      this.$nextTick(() => {
        setTimeout(function () {
          const element = that.$refs.syncTransStepContainer
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    }
  },
  watch: {
  },
  async mounted () {
    await this.getLocations()
    const appId = this.printRequest.dealerSqAppId
    const cardButton = document.getElementById('card-button')
    cardButton.disabled = true

    let payments
    try {
      payments = window.Square.payments(appId, this.locationId)
    } catch (e) {
      const statusContainer = document.getElementById('payment-status-container')
      statusContainer.className = 'missing-credentials mt-10'
      statusContainer.style.visibility = 'visible'
      this.serverErrorMessage = e.message
      return
    }

    if (!window.Square) {
      this.serverErrorMessage = 'Square.js failed to load properly'
      throw new Error('Square.js failed to load properly')
    }

    try {
      this.card = await this.initializeCard(payments)
      cardButton.disabled = false
    } catch (e) {
      this.serverErrorMessage = e.message
    }

    try {
      this.applePay = await this.initializeApplePay(payments)
    } catch (e) {
      console.error('Initializing Apple Pay failed', e)
      this.hasApplePayInitialized = false
      // There are a number of reason why Apple Pay may not be supported
      // (e.g. Browser Support, Device Support, Account). Therefore you should handle
      // initialization failures, while still loading other applicable payment methods.
    }

    try {
      this.googlePay = await this.initializeGooglePay(payments)
    } catch (e) {
      console.error('Initializing Google Pay failed', e)
      this.hasGooglePayInitialized = false
      // There are a number of reason why Google Pay may not be supported
      // (e.g. Browser Support, Device Support, Account). Therefore you
      // should handle initialization failures, while still loading other
      // applicable payment methods.
    }
  }
}
</script>

<style scoped>
  .modal-card-head, .modal-card-foot {
    padding: 20px !important;
  }

  .modal-card {
    overflow: auto;
    line-height: 1.5rem !important;
  }

  .modal-card-body {
    line-height: 1.25rem !important;
    padding: 20px !important;
    height: calc(80vh - 10px);
    overflow-y: auto !important;
  }

  @media screen and (max-width: 539px) {
    .notification-container {
      padding: 0.5rem !important;
      margin: 0.5rem !important;
      font-size: 0.85rem !important;
    }
  }

  @media screen and (max-width: 539px) {
    .notification-container {
      padding: 0.5rem !important;
      margin: 0.5rem !important;
      font-size: 0.85rem !important;
    }
  }

  @media screen and (min-height: 790px), print {
    .modal-card-body {
      position:relative;
      height: calc(50vh - 80px);
    }
  }

  @media screen and (max-width: 539px) and (min-height: 790px) {
  .modal-card-body {
      line-height: 1.25rem !important;
      padding: 20px !important;
      height: calc(80vh - 10px);
      overflow-y: auto !important;
    }
  }
</style>
